// @flow

import * as React from 'react';
import { graphql, useFragment } from 'react-relay';
import { useLocale } from '../../controls/locale';
import { createDescriptor, useRouter } from '../../controls/router';
import { Breadcrumbs, JsonLD } from '../../controls/breadcrumbs';
import { translatePropertyMainType } from '../translate/propertyTypes';
import {
  placesDescriptor,
  offerTypeDescriptor,
  roomsFilterDescriptor,
  pageDescriptor,
} from './Filters/sharedDescriptors';
import { propertyTypeFilterDescriptor } from './Filters';
import type { AggregatesListingsBreadcrumbs_aggregatesListings$key } from './__generated__/AggregatesListingsBreadcrumbs_aggregatesListings.graphql';

type Props = {|
  aggregatesListings: ?AggregatesListingsBreadcrumbs_aggregatesListings$key,
  className?: string,
  css?: any,
  customPageTitle?: string,
|};

const descriptor = createDescriptor(
  placesDescriptor,
  offerTypeDescriptor,
  propertyTypeFilterDescriptor,
  roomsFilterDescriptor,
  pageDescriptor,
);

const PROPERTY_TYPES_TO_IGNORE = ['HOUSE_APPT', 'OTHER'];

const makeRel = (propertyType, canonicalPlace = null) =>
  PROPERTY_TYPES_TO_IGNORE.includes(propertyType) || canonicalPlace != null
    ? 'nofollow'
    : undefined;

const offerTypeItem = (t, params, makeHref) => ({
  label: params.offerType === 'buy' ? t('buy') : t('rentVerb'),
  ...makeHref({
    offerType: params.offerType,
    placeSlugs: [],
    compositePropertyType_eq: null,
    numberOfRooms_gte: null,
    numberOfRooms_lte: null,
  }),
  rel: makeRel(params.compositePropertyType_eq),
});

const propertyTypeItem = (t, params, makeHref) => {
  const propertyType = params.compositePropertyType_eq;

  if (propertyType == null) {
    return null;
  }

  const { propertyTypeSingularCapital } = translatePropertyMainType(
    t,
    propertyType,
  );

  return {
    label: propertyTypeSingularCapital,
    ...makeHref({
      placeSlugs: [],
      numberOfRooms_gte: null,
      numberOfRooms_lte: null,
      offerType: params.offerType,
      compositePropertyType_eq: propertyType,
    }),
    rel: makeRel(propertyType),
  };
};

const placeItems = (t, _places, params, makeHref) => {
  const places = _places ?? [];
  const place = places.length === 1 ? places[0] : null;

  if (place == null) {
    return [];
  }

  const baseParams = {
    offerType: params.offerType,
    compositePropertyType_eq: params.compositePropertyType_eq,
  };

  return place.breadcrumbs
    .map(p => {
      return {
        label: p.label,
        ...makeHref({
          ...baseParams,
          placeSlugs: [
            {
              slug: p.slug,
              lang: p.lang,
            },
          ],
          numberOfRooms_gte: null,
          numberOfRooms_lte: null,
        }),
        rel: makeRel(params.compositePropertyType_eq, p.canonicalPlace),
      };
    })
    .reverse();
};

const roomsItem = (t, params, makeHref) => {
  const { numberOfRooms_gte, numberOfRooms_lte } = params;

  const hrefProps = makeHref(params);
  const rel = makeRel(params.compositePropertyType_eq);

  if (numberOfRooms_lte == null) {
    if (numberOfRooms_gte == null) {
      return null;
    }

    return {
      label: t('roomsFromSelected', { count: numberOfRooms_gte }),
      ...hrefProps,
      rel,
    };
  }

  if (numberOfRooms_gte == null) {
    // our filters UI does not allow this case,
    // so we don't support it here either
    return null;
  }

  if (numberOfRooms_lte - numberOfRooms_gte <= 0.5) {
    return {
      label: t('roomsSingleSelected', { count: numberOfRooms_gte }),
      ...hrefProps,
      rel,
    };
  }

  return {
    label: t('roomsMultiSelected', {
      from: numberOfRooms_gte,
      to: Math.floor(numberOfRooms_lte),
    }),
    ...hrefProps,
    rel,
  };
};

export const AggregatesListingsBreadcrumbs = (props: Props): React.Node => {
  const router = useRouter();
  const { t } = useLocale();

  const [descriptorState, setDescriptorState, flush] = descriptor.useState();
  const placeSlugs = descriptorState.placeSlugs ?? [];

  const aggregatesListings = useFragment(
    graphql`
      fragment AggregatesListingsBreadcrumbs_aggregatesListings on AggregatesListingConnectionConnection {
        filters {
          places {
            breadcrumbs {
              slug
              lang
              label
              canonicalPlace {
                slug
              }
            }
          }
        }
      }
    `,
    placeSlugs.length === 1 ? props.aggregatesListings : null,
  );

  const makeHref = params => {
    const {
      placeSlugs,
      offerType,
      compositePropertyType_eq,
      numberOfRooms_gte,
      numberOfRooms_lte,
      page,
      ...baseQuery
    } = descriptorState;
    const query = {
      ...(baseQuery: { [string]: string, ... }),
      ...descriptor.getQueryParams(params),
    };
    return {
      href: {
        pathname: props.customPageTitle == null ? router.pathname : '/listings',
        query,
      },
      onClick: () => {
        if (props.customPageTitle == null) {
          setDescriptorState({ ...params, page: 0 });
          flush();
        }
      },
    };
  };

  const items = [
    {
      label: '',
      isHome: true,
      href: { pathname: '/index', query: {} },
      onClick: () => {},
      rel: makeRel(descriptorState.compositePropertyType_eq),
    },
    offerTypeItem(t, descriptorState, makeHref),
    propertyTypeItem(t, descriptorState, makeHref),
    ...(aggregatesListings != null
      ? placeItems(
          t,
          aggregatesListings.filters.places,
          descriptorState,
          makeHref,
        )
      : []),
    props.customPageTitle == null
      ? roomsItem(t, descriptorState, makeHref)
      : null,
    ...(props.customPageTitle != null
      ? [
          {
            label: props.customPageTitle,
            href: { pathname: router.pathname },
          },
        ]
      : []),
  ].filter(Boolean);

  return (
    <>
      <JsonLD items={items} />
      <Breadcrumbs className={props.className} items={items} />
    </>
  );
};
